import { Box, CircularProgress, Typography } from "@mui/material";
import { useFormikContext } from "formik";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery } from "react-query";

import { useDebounce } from "../hooks/debounce";
import ImpactSummary from "./ImpactSummary";
import { fetchActivationImpact } from "./api";
import { AssignmentScope } from "./types";

// Select fields hand back either the picked object or a bare id depending on
// where the value came from (fresh pick vs. loaded row).
const idOf = (value: unknown): number | null => {
  if (value == null) {
    return null;
  }
  if (typeof value === "object") {
    const candidate = value as { id?: number };
    return candidate.id ?? null;
  }
  return Number(value);
};

const scopeOf = (values: Record<string, unknown>): AssignmentScope => ({
  uh_type_nandc: idOf(values.uh_type_nandc),
  uh_cat: idOf(values.uh_cat),
  grid_type: idOf(values.grid_type),
  date_from: (values.date_from as string) || null,
  date_to: (values.date_to as string) || null,
  weekdays: (values.weekdays as string) || undefined,
});

// The dry-run walks the price table over a two-year horizon, so it waits for
// the analyst to stop changing the scope rather than firing on every keystroke.
const SETTLE_MS = 600;

/**
 * "What would activating this do to my prices?", inside the assignment form.
 *
 * It answers on its own: an analyst who has to press a button to find out reads
 * the number once, then stops pressing, and the figure on screen quietly stops
 * describing the scope beside it. Keyed on the scope, so a stale answer cannot
 * survive an edit — react-query drops it the moment the key changes.
 */
const ActivationImpactPanel = () => {
  const intl = useIntl();
  const { values } = useFormikContext<Record<string, unknown>>();

  const gridId = idOf(values.grid);
  const scope = scopeOf(values);
  const settled = useDebounce(JSON.stringify([gridId, scope]), SETTLE_MS);

  const simulation = useQuery(
    ["pricing-tier-activation-impact", settled],
    () => fetchActivationImpact(gridId as number, scope),
    { enabled: gridId != null, keepPreviousData: false, retry: false }
  );

  if (gridId == null) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {simulation.isLoading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            <FormattedMessage id="PRICING_TIER_SIMULATE_ACTIVATION" />
          </Typography>
        </Box>
      )}

      {simulation.isError && (
        <Typography variant="caption" color="error.main">
          {simulation.error instanceof Error
            ? simulation.error.message
            : intl.formatMessage({ id: "ERROR_OCCURRED" })}
        </Typography>
      )}

      <ImpactSummary
        report={simulation.data ?? null}
        isLoading={false}
        compact
      />
    </Box>
  );
};

export default ActivationImpactPanel;
